import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ReplayIcon from '@mui/icons-material/Replay';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';

import {
  AccountTable,
  DashboardError,
  DashboardPage,
  HiringTeamPanel,
  IAccountTableColumn,
  ListingDetails,
} from '../../components/JobsAccount';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { book } from '../../routers/books';
import {
  IGridRequest,
  IJobListing,
} from '../../services/serviceJobsAccount/interfaces';
import {
  useGetActiveJobsQuery,
  useGetInactiveJobsQuery,
} from '../../services/serviceJobsAccount';
import {
  getEditJobPath,
  getPrepaidPurchasePath,
  getReactivateJobPath,
  getUpgradeJobPath,
} from '../../utils/jobAccountRoutes';
import { isForbidden } from '../../utils/jobAccessError';

const PAGE_SIZE = 10;

const initialRequest = (): IGridRequest => ({
  page: 1,
  pageSize: PAGE_SIZE,
  sortBy: 'Dated',
  sortDirection: 'desc',
});

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

export const JobsListing = () => {
  const history = useHistory();
  const [tab, setTab] = useState<'active' | 'inactive'>('active');
  const [activeRequest, setActiveRequest] = useState<IGridRequest>(initialRequest);
  const [inactiveRequest, setInactiveRequest] = useState<IGridRequest>(initialRequest);
  const active = useGetActiveJobsQuery(activeRequest);
  const inactive = useGetInactiveJobsQuery(inactiveRequest);

  const columns = (isActive: boolean): IAccountTableColumn<IJobListing>[] => [
    {
      id: 'details',
      label: 'Details',
      render: (listing) => (
        <ListingDetails listing={listing} isActive={isActive} />
      ),
    },
    {
      id: 'dated',
      label: isActive ? 'Expires' : 'Created',
      sortBy: 'Dated',
      width: 130,
      render: (listing) => formatDate(listing.dated),
    },
    {
      id: 'applicants',
      label: 'Applicants',
      sortBy: 'ApplicantCount',
      align: 'center',
      width: 120,
      render: (listing) => (
        <Button
          size="small"
          onClick={() => history.push(book.jobsApplicants(listing.eguid))}
          aria-label={`View ${listing.applicantCount} applicants`}
        >
          {listing.applicantCount}
        </Button>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: isActive ? 320 : 220,
      render: (listing) => (
        <Stack
          direction="row"
          spacing={0.75}
          justifyContent="flex-end"
          alignItems="center"
        >
          {isActive ? (
            <>
              {!listing.isFeatured ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StarOutlineIcon fontSize="small" />}
                  onClick={() => history.push(getUpgradeJobPath(
                    listing.siteId,
                    listing.eguid
                  ))}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Upgrade to Featured
                </Button>
              ) : null}
              <HiringTeamPanel
                eguid={listing.eguid}
                compact
                listingTitle={listing.title}
              />
              <Tooltip title="View applicants">
                <IconButton
                  size="small"
                  onClick={() => history.push(book.jobsApplicants(listing.eguid))}
                  aria-label="View applicants"
                >
                  <PeopleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit listing">
                <IconButton
                  size="small"
                  onClick={() => history.push(getEditJobPath(listing.siteId, listing.eguid))}
                  aria-label="Edit listing"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<ReplayIcon fontSize="small" />}
              onClick={() => history.push(getReactivateJobPath(
                listing.siteId,
                listing.eguid
              ))}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Reactivate Listing
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  const selected = tab === 'active' ? active : inactive;
  const request = tab === 'active' ? activeRequest : inactiveRequest;
  const setRequest = tab === 'active' ? setActiveRequest : setInactiveRequest;
  const accessDenied = isForbidden(active.error) || isForbidden(inactive.error);

  return (
    <DashboardPage
      title="Job Listings"
      noAccessComponent={accessDenied ? <NoAccessMessage /> : undefined}
      actions={(
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            component={Link}
            to={getPrepaidPurchasePath(null)}
            variant="outlined"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Buy a Job Package
          </Button>
          <Button
            component={Link}
            to={book.postAJob}
            variant="contained"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Post New Job Listing
          </Button>
        </Stack>
      )}
    >
      <Tabs
        value={tab}
        onChange={(_event, value: 'active' | 'inactive') => setTab(value)}
        sx={{ mb: 2 }}
        aria-label="Job listing status"
      >
        <Tab label={`Active (${active.data?.total || 0})`} value="active" />
        <Tab label={`Expired / Inactive (${inactive.data?.total || 0})`} value="inactive" />
      </Tabs>

      <DashboardError error={selected.error} />

      <AccountTable
        ariaLabel={`${tab} job listings`}
        columns={columns(tab === 'active')}
        rows={selected.data?.result || []}
        getRowId={(listing) => listing.id}
        emptyMessage={tab === 'active'
          ? 'You do not have any active job listings.'
          : 'You do not have any expired or inactive job listings.'}
        isLoading={selected.isLoading || selected.isFetching}
        page={request.page}
        pageSize={request.pageSize}
        total={selected.data?.total || 0}
        sortBy={request.sortBy}
        sortDirection={request.sortDirection}
        onPageChange={(page) => setRequest((current) => ({ ...current, page }))}
        onSort={(sortBy, sortDirection) => setRequest((current) => ({
          ...current,
          page: 1,
          sortBy,
          sortDirection,
        }))}
      />
    </DashboardPage>
  );
};

export default JobsListing;
